import { Grid3x3, Link2, Unlink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';

import { ElegirLicencias } from './ElegirLicencias';
import { ElementoModal } from './ElementoModal';
import { FiltrosBiblioteca } from './FiltrosBiblioteca';
import { MenuNuevo } from './MenuNuevo';
import { NuevoCompuesto } from './NuevoCompuesto';
import { useAsignar, useBiblioteca, useElemento, useResumenBiblioteca } from './queries';
import { AltaMasiva } from './simples/AltaMasiva';
import { ModalSimple } from './simples/ModalSimple';
import { OPCIONES_COMPUESTAS, rutaCompuesto, SIMPLES, type OpcionNuevo } from './tipos';

/**
 * Abre el editor de un elemento: modal del tipo simple, pantalla del compuesto o, si el tipo no
 * tiene editor (podcast, datos raros), el modal de datos comunes.
 */
function AbrirElemento({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const navigate = useNavigate();
  const q = useElemento(id);
  const d = q.data && q.data.id === id ? q.data : null;
  const ruta = d?.detail ? rutaCompuesto(d.type, d.detail.id) : null;
  useEffect(() => {
    if (ruta) void navigate(ruta);
  }, [ruta, navigate]);
  if (id === null || ruta) return null;
  if (d && d.detail && SIMPLES[d.type]) {
    return <ModalSimple tipo={d.type} id={d.detail.id} abierto onCerrar={onCerrar} />;
  }
  if (!d && !q.isError) return null;
  return <ElementoModal id={id} onCerrar={onCerrar} />;
}

const CLAVES = ['q', 'type', 'specialtyId', 'membershipId', 'english', 'unassigned'] as const;

/** Agregar o quitar los elementos elegidos de varias licencias a la vez. */
function ModalAsignar({
  accion,
  ids,
  onCerrar,
  onListo,
}: {
  accion: 'add' | 'remove' | null;
  ids: number[];
  onCerrar: () => void;
  onListo: () => void;
}) {
  const resumen = useResumenBiblioteca();
  const asignar = useAsignar();
  const [elegidas, setElegidas] = useState<Set<number>>(new Set());
  const cerrar = () => {
    setElegidas(new Set());
    onCerrar();
  };
  return (
    <Modal
      abierto={accion !== null}
      onCerrar={cerrar}
      titulo={accion === 'remove' ? 'Quitar de licencias' : 'Agregar a licencias'}
      descripcion={`${ids.length} elemento(s) seleccionado(s). Los alumnos de esas licencias ${
        accion === 'remove' ? 'dejarán de ver' : 'verán'
      } este contenido después del corte M2.`}
      ancho="max-w-2xl"
    >
      {resumen.data && (
        <ElegirLicencias
          licencias={resumen.data.memberships}
          elegidas={elegidas}
          onCambio={(id, marcada) =>
            setElegidas((s) => {
              const n = new Set(s);
              if (marcada) n.add(id);
              else n.delete(id);
              return n;
            })
          }
        />
      )}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className="btn-cs btn-contorno" onClick={cerrar}>
          Cancelar
        </button>
        <button
          type="button"
          className={accion === 'remove' ? 'btn-cs btn-peligro' : 'btn-cs btn-primario'}
          disabled={elegidas.size === 0 || asignar.isPending || accion === null}
          onClick={() =>
            accion &&
            asignar.mutate(
              { action: accion, libraryItemIds: ids, membershipIds: [...elegidas] },
              {
                onSuccess: (r) => {
                  toast.success(
                    r.changed === 0
                      ? 'No hubo cambios: ya estaba así.'
                      : `${r.changed} asignación(es) ${accion === 'remove' ? 'quitada(s)' : 'agregada(s)'}.`,
                  );
                  setElegidas(new Set());
                  onListo();
                },
              },
            )
          }
        >
          {accion === 'remove' ? 'Quitar' : 'Agregar'} ({elegidas.size})
        </button>
      </div>
    </Modal>
  );
}

function Biblioteca() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const editable = useCan('contenido', 'editar');
  const resumen = useResumenBiblioteca();
  const q = useBiblioteca({ ...query, page: filtros.page, pageSize: 50 });
  const [abierto, setAbierto] = useState<number | null>(null);
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [accion, setAccion] = useState<'add' | 'remove' | null>(null);
  const [nuevo, setNuevo] = useState<OpcionNuevo | null>(null);
  const nombreLicencia = new Map(resumen.data?.memberships.map((m) => [m.id, m.name]));

  const pagina = q.data?.data ?? [];
  const todosEnPagina = pagina.length > 0 && pagina.every((e) => seleccion.has(e.id));
  const marcar = (ids: number[], si: boolean) =>
    setSeleccion((s) => {
      const n = new Set(s);
      ids.forEach((id) => (si ? n.add(id) : n.delete(id)));
      return n;
    });

  return (
    <>
      <Encabezado
        titulo="Biblioteca"
        descripcion="Todo el contenido (clases, presentaciones, videos, guías, exámenes…) y las licencias que lo incluyen. Haz clic en un nombre para editarlo."
        acciones={
          <>
            <Link to="/contenido/matriz" className="btn-cs btn-vidrio">
              <Grid3x3 size={16} aria-hidden /> Contenido × licencia
            </Link>
            {editable && <MenuNuevo compuestos={OPCIONES_COMPUESTAS} onElegir={setNuevo} />}
          </>
        }
      />
      <FiltrosBiblioteca filtros={filtros} cambiar={cambiar} claves={CLAVES} />

      {editable && seleccion.size > 0 && (
        <div className="panel-vidrio mb-4 flex flex-wrap items-center gap-3 py-3">
          <span className="text-sm font-bold">{seleccion.size} seleccionado(s)</span>
          <button type="button" className="btn-cs btn-primario" onClick={() => setAccion('add')}>
            <Link2 size={16} aria-hidden /> Agregar a licencias
          </button>
          <button type="button" className="btn-cs btn-vidrio" onClick={() => setAccion('remove')}>
            <Unlink size={16} aria-hidden /> Quitar de licencias
          </button>
          <button
            type="button"
            className="btn-cs btn-contorno ml-auto"
            onClick={() => setSeleccion(new Set())}
          >
            Limpiar selección
          </button>
        </div>
      )}

      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin contenido con esos filtros" />}
      {pagina.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                {editable && (
                  <th className="w-8">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[var(--color-azul-600)]"
                      aria-label="Seleccionar la página"
                      checked={todosEnPagina}
                      onChange={(e) =>
                        marcar(
                          pagina.map((x) => x.id),
                          e.target.checked,
                        )
                      }
                    />
                  </th>
                )}
                <th>ID</th>
                <th>Tipo</th>
                <th>Nombre</th>
                <th>Especialidad</th>
                <th>Idioma</th>
                <th>Licencias</th>
              </tr>
            </thead>
            <tbody>
              {pagina.map((e) => (
                <tr key={e.id}>
                  {editable && (
                    <td>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[var(--color-azul-600)]"
                        aria-label={`Seleccionar ${e.name}`}
                        checked={seleccion.has(e.id)}
                        onChange={(ev) => marcar([e.id], ev.target.checked)}
                      />
                    </td>
                  )}
                  <td className="tabular">{e.id}</td>
                  <td className="whitespace-nowrap">{e.typeLabel}</td>
                  <td className="max-w-md">
                    <button
                      type="button"
                      className="text-left font-semibold hover:underline"
                      onClick={() => setAbierto(e.id)}
                    >
                      {e.name}
                    </button>
                    {e.tags.length > 0 && (
                      <p className="text-xs text-texto-3">{e.tags.join(' · ')}</p>
                    )}
                  </td>
                  <td>{e.specialtyName ?? '—'}</td>
                  <td>{e.isEnglish ? 'Inglés' : 'Español'}</td>
                  <td
                    className="tabular"
                    title={e.membershipIds
                      .map((id) => nombreLicencia.get(id) ?? `#${id}`)
                      .join('\n')}
                  >
                    {e.membershipIds.length === 0 ? (
                      <span className="insignia insignia-exclusivo">Ninguna</span>
                    ) : (
                      e.membershipIds.length
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      <AbrirElemento id={abierto} onCerrar={() => setAbierto(null)} />
      <ModalSimple
        tipo={nuevo && !nuevo.masivo && SIMPLES[nuevo.tipo] ? nuevo.tipo : null}
        id={null}
        abierto={nuevo !== null && !nuevo.masivo && SIMPLES[nuevo.tipo] !== undefined}
        onCerrar={() => setNuevo(null)}
      />
      <NuevoCompuesto
        tipo={nuevo && !SIMPLES[nuevo.tipo] ? nuevo.tipo : null}
        onCerrar={() => setNuevo(null)}
      />
      <AltaMasiva
        tipo={nuevo?.masivo ? (nuevo.tipo as 'flashcard' | 'guia-gpc') : null}
        onCerrar={() => setNuevo(null)}
      />
      <ModalAsignar
        accion={accion}
        ids={[...seleccion]}
        onCerrar={() => setAccion(null)}
        onListo={() => {
          setAccion(null);
          setSeleccion(new Set());
        }}
      />
    </>
  );
}

export function BibliotecaPage() {
  return (
    <PermissionGate seccion="contenido">
      <Biblioteca />
    </PermissionGate>
  );
}
